import { writable } from 'svelte/store';
import { Document as FlexSearchDocument } from 'flexsearch';

export interface SearchDocument {
  id: string;
  path: string;
  title: string;
  content: string;
  type: 'volume' | 'wiki';
}

export interface SearchResult {
  path: string;
  title: string;
  type: 'volume' | 'wiki';
  excerpt: string;
}

// Search state
export const searchQuery = writable('');
export const searchResults = writable<SearchResult[]>([]);
export const isSearching = writable(false);
export const searchIndexLoaded = writable(false);

// FlexSearch index
// FlexSearch Document typed loosely to avoid complex generic constraints
let index: FlexSearchDocument<any> | null = null;
let documents: SearchDocument[] = [];

// Load search index from server
export async function loadSearchIndex(): Promise<void> {
  if (index) return; // Already loaded

  try {
    const response = await fetch('/search-index.json');
    if (!response.ok) throw new Error('Failed to load search index');

    documents = await response.json();

    // Create FlexSearch index
    index = new FlexSearchDocument<any>({
      document: {
        id: 'id',
        index: ['title', 'content'],
        store: ['path', 'title', 'type', 'content'],
      },
      tokenize: 'forward',
      context: true,
    });

    // Add documents to index
    for (const doc of documents) {
      index.add(doc);
    }

    searchIndexLoaded.set(true);
  } catch (error) {
    console.error('Error loading search index:', error);
  }
}

// Extract excerpt around the match
function getExcerpt(content: string, query: string, maxLength = 150): string {
  const lowerContent = content.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const pos = lowerContent.indexOf(lowerQuery);

  if (pos === -1) {
    // No direct match, return beginning of content
    return content.slice(0, maxLength) + (content.length > maxLength ? '...' : '');
  }

  // Find excerpt around the match
  const start = Math.max(0, pos - 50);
  const end = Math.min(content.length, pos + query.length + 100);

  let excerpt = content.slice(start, end);
  if (start > 0) excerpt = '...' + excerpt;
  if (end < content.length) excerpt = excerpt + '...';

  return excerpt;
}

// Perform search
export async function search(query: string): Promise<void> {
  searchQuery.set(query);

  if (!query.trim()) {
    searchResults.set([]);
    return;
  }

  // Lazy load index
  if (!index) {
    isSearching.set(true);
    await loadSearchIndex();
  }

  if (!index) {
    searchResults.set([]);
    isSearching.set(false);
    return;
  }

  isSearching.set(true);

  try {
    // Search in both title and content
    const titleResults = index.search(query, {
      index: 'title',
      limit: 20,
    });

    const contentResults = index.search(query, {
      index: 'content',
      limit: 20,
    });

    // Merge results, prioritizing title matches
    const seenPaths = new Set<string>();
    const results: SearchResult[] = [];

    // Add title matches first (higher priority)
    for (const result of titleResults) {
      for (const id of result.result) {
        const doc = documents.find(d => d.id === id);
        if (doc && !seenPaths.has(doc.path)) {
          seenPaths.add(doc.path);
          results.push({
            path: doc.path,
            title: doc.title,
            type: doc.type,
            excerpt: getExcerpt(doc.content, query),
          });
        }
      }
    }

    // Add content matches
    for (const result of contentResults) {
      for (const id of result.result) {
        const doc = documents.find(d => d.id === id);
        if (doc && !seenPaths.has(doc.path)) {
          seenPaths.add(doc.path);
          results.push({
            path: doc.path,
            title: doc.title,
            type: doc.type,
            excerpt: getExcerpt(doc.content, query),
          });
        }
      }
    }

    searchResults.set(results);
  } catch (error) {
    console.error('Search error:', error);
    searchResults.set([]);
  } finally {
    isSearching.set(false);
  }
}

// Debounced search
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

export function debouncedSearch(query: string, delay = 200): void {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = setTimeout(() => {
    search(query);
  }, delay);
}
